/**
 * CANONICAL PLANS DEFINITION — Single Source of Truth
 * 
 * Every system that touches pricing, entitlements, checkout, or export gating
 * MUST import from this file. Never define price IDs or tier logic elsewhere.
 * 
 * Keyed by Stripe Price ID for O(1) entitlement resolution.
 */

// ── Tier types ────────────────────────────────────────────────────────────────

export type PlanTier = 'free' | 'student' | 'pro' | 'team' | 'enterprise';
export type BillingInterval = 'monthly' | 'annual';

export interface PlanLimits {
  transcripts: number;        // -1 = unlimited
  analysesPerMonth: number;   // -1 = unlimited
  audioMinutesPerMonth: number; // -1 = unlimited
  exportsPerMonth: number;    // -1 = unlimited, 0 = none
  exports: 'none' | 'basic' | 'all';
  support: 'community' | 'email' | 'priority' | 'dedicated';
}

export interface PlanDefinition {
  tier: PlanTier;
  /** @deprecated Use `tier` instead */
  id: PlanTier;
  name: string;
  description: string;
  monthlyPriceCents: number;
  annualPriceCents: number;
  /** Dollar amount for backward compat */
  monthlyPrice: number;
  /** Dollar amount for backward compat */
  annualPrice: number;
  /** @deprecated Use `monthlyPriceCents === 0` check */
  productId: string | null;
  /** @deprecated */
  annualProductId?: string | null;
  monthlyPriceId: string | null;
  annualPriceId: string | null;
  limits: PlanLimits;
  features: string[];
  isExportEnabled: boolean;
  highlighted?: boolean;
  comingSoon?: boolean;
  contactSales?: boolean;
  cta: string;
}

// ── Price-to-Tier mapping (the core lookup) ───────────────────────────────────

export interface PriceMapping {
  tier: PlanTier;
  interval: BillingInterval;
  isExportEnabled: boolean;
}

/**
 * Master price-ID → tier mapping.
 * This is THE authority for resolving a Stripe price ID to entitlements.
 * If a price ID is not in this map, the user gets Free tier.
 */
const PRICE_TO_TIER: Record<string, PriceMapping> = {
  // Student monthly
  'price_1SxQp936ErS8cGmLplVZNy38': { tier: 'student', interval: 'monthly', isExportEnabled: true },
  // Student annual
  'price_1SxQuv36ErS8cGmLc3orsz1O': { tier: 'student', interval: 'annual', isExportEnabled: true },
  // Pro monthly
  'price_1SnYv536ErS8cGmLLG3xHdX8': { tier: 'pro', interval: 'monthly', isExportEnabled: true },
  // Pro annual
  'price_1T55Fh36ErS8cGmLzkW5EVIa': { tier: 'pro', interval: 'annual', isExportEnabled: true },
};

// Freeze to prevent runtime mutation
Object.freeze(PRICE_TO_TIER);

/**
 * Resolve a Stripe price ID to tier + entitlements.
 * Returns null if the price ID is unknown (→ treat as Free).
 */
export function resolveTierByPriceId(priceId: string | null | undefined): PriceMapping | null {
  if (!priceId) return null;
  return PRICE_TO_TIER[priceId] ?? null;
}

/**
 * Get all known valid price IDs. Useful for runtime validation.
 */
export function getAllValidPriceIds(): string[] {
  return Object.keys(PRICE_TO_TIER);
}

/**
 * Check if a price ID is known and export-enabled.
 */
export function isExportEnabledByPriceId(priceId: string | null | undefined): boolean {
  if (!priceId) return false;
  return PRICE_TO_TIER[priceId]?.isExportEnabled ?? false;
}

// ── Plan Definitions (for UI and feature display) ─────────────────────────────

export const PLANS: PlanDefinition[] = [
  {
    tier: 'free',
    id: 'free',
    name: 'Free',
    description: 'Try the workflow',
    monthlyPriceCents: 0,
    annualPriceCents: 0,
    monthlyPrice: 0,
    annualPrice: 0,
    productId: null,
    monthlyPriceId: null,
    annualPriceId: null,
    limits: {
      transcripts: 2,
      analysesPerMonth: 5,
      audioMinutesPerMonth: 10,
      exportsPerMonth: 2,
      exports: 'basic',
      support: 'community',
    },
    features: [
      'Up to 2 transcripts',
      'Up to 5 AI analyses per month',
      'Up to 10 audio minutes per month',
      'In-app viewing and refining',
      '2 exports per month',
    ],
    isExportEnabled: true,
    cta: 'Get started',
  },
  {
    tier: 'student',
    id: 'student',
    name: 'Student',
    description: 'For thesis & coursework',
    monthlyPriceCents: 1200,
    annualPriceCents: 9900,
    monthlyPrice: 12,
    annualPrice: 99,
    productId: 'prod_TvHNs0bCWxKNDs',
    annualProductId: 'prod_TvHT5xX4T83FJQ',
    monthlyPriceId: 'price_1SxQp936ErS8cGmLplVZNy38',
    annualPriceId: 'price_1SxQuv36ErS8cGmLc3orsz1O',
    limits: {
      transcripts: 10,
      analysesPerMonth: 40,
      audioMinutesPerMonth: 60,
      exportsPerMonth: 20,
      exports: 'all',
      support: 'email',
    },
    features: [
      'Up to 10 transcripts',
      'Up to 40 AI analyses per month',
      'Up to 60 audio minutes per month',
      'Exports included (PDF, DOCX, CSV)',
      'Full citation styles',
    ],
    isExportEnabled: true,
    cta: 'Choose Student',
  },
  {
    tier: 'pro',
    id: 'pro',
    name: 'Pro',
    description: 'For publish-ready work',
    monthlyPriceCents: 2900,
    annualPriceCents: 27800,
    monthlyPrice: 29,
    annualPrice: 278,
    productId: 'prod_Tl559nvDaKaZie',
    annualProductId: 'prod_U3BcsgxbbiSQrX',
    monthlyPriceId: 'price_1SnYv536ErS8cGmLLG3xHdX8',
    annualPriceId: 'price_1T55Fh36ErS8cGmLzkW5EVIa',
    limits: {
      transcripts: 25,
      analysesPerMonth: 100,
      audioMinutesPerMonth: 120,
      exportsPerMonth: -1,
      exports: 'all',
      support: 'email',
    },
    features: [
      'Up to 25 transcripts',
      'Up to 100 AI analyses per month',
      'Up to 120 audio minutes per month',
      'Exports included (PDF, DOCX, CSV)',
      'Full citation styles',
      'Priority support',
      'Prepaid credits available',
    ],
    isExportEnabled: true,
    highlighted: true,
    cta: 'Choose Pro',
  },
  {
    tier: 'team',
    id: 'team',
    name: 'Teams',
    description: 'For labs & teams',
    monthlyPriceCents: 0,
    annualPriceCents: 0,
    monthlyPrice: 0,
    annualPrice: 0,
    productId: null,
    monthlyPriceId: null,
    annualPriceId: null,
    limits: {
      transcripts: -1,
      analysesPerMonth: -1,
      audioMinutesPerMonth: -1,
      exportsPerMonth: -1,
      exports: 'all',
      support: 'priority',
    },
    features: [
      'Shared projects and datasets',
      'Team collaboration workflows',
      'Shared codebooks',
      'Role-based access',
      'Priority support',
    ],
    isExportEnabled: true,
    comingSoon: true,
    cta: 'Join waitlist',
  },
  {
    tier: 'enterprise',
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For institutions & organizations',
    monthlyPriceCents: 0,
    annualPriceCents: 0,
    monthlyPrice: 0,
    annualPrice: 0,
    productId: null,
    monthlyPriceId: null,
    annualPriceId: null,
    limits: {
      transcripts: -1,
      analysesPerMonth: -1,
      audioMinutesPerMonth: -1,
      exportsPerMonth: -1,
      exports: 'all',
      support: 'dedicated',
    },
    features: [
      'Custom usage limits',
      'Advanced security & compliance',
      'Dedicated onboarding',
      'SSO integration',
      'Data residency options',
      'Custom support & SLAs',
    ],
    isExportEnabled: true,
    contactSales: true,
    cta: 'Contact sales',
  },
];

// ── Derived helpers (computed from PLANS, never hardcoded) ─────────────────────

/** Get a plan definition by tier ID */
export function getPlanByTier(tier: PlanTier): PlanDefinition {
  return PLANS.find(p => p.tier === tier) ?? PLANS[0]; // fallback to free
}

/** Get a plan definition by price ID (monthly or annual) */
export function getPlanByPriceId(priceId: string): PlanDefinition | undefined {
  return PLANS.find(p => p.monthlyPriceId === priceId || p.annualPriceId === priceId);
}

/** Get the limits for a tier */
export function getLimitsByTier(tier: PlanTier): PlanLimits {
  return getPlanByTier(tier).limits;
}

/** Check if a tier has export capabilities */
export function canExport(tier: PlanTier): boolean {
  return getPlanByTier(tier).isExportEnabled;
}

/** Compute annual savings percentage for a plan */
export function getAnnualSavings(plan: PlanDefinition): number {
  if (plan.monthlyPriceCents === 0) return 0;
  const monthlyTotal = plan.monthlyPriceCents * 12;
  return Math.round(((monthlyTotal - plan.annualPriceCents) / monthlyTotal) * 100);
}

/** Get monthly equivalent price in dollars for display */
export function getMonthlyDisplayPrice(plan: PlanDefinition, interval: BillingInterval): number {
  if (interval === 'annual' && plan.annualPriceCents > 0) {
    return Math.round(plan.annualPriceCents / 12) / 100;
  }
  return plan.monthlyPriceCents / 100;
}

/** Get total annual price in dollars for display */
export function getAnnualDisplayPrice(plan: PlanDefinition): number {
  return plan.annualPriceCents / 100;
}

// ── Credit Packs (one-time purchases) ────────────────────────────────────────

export interface CreditPack {
  id: string;
  type: 'analysis' | 'audio';
  amount: number;
  priceCents: number;
  /** @deprecated Use priceCents */
  price: number;
  priceId: string;
  label: string;
}

export const CREDIT_PACKS: CreditPack[] = [
  { id: 'ai_50', type: 'analysis', amount: 50, priceCents: 1000, price: 1000, priceId: 'price_1SuFSv36ErS8cGmLJDdo2MDK', label: '50 AI Analyses' },
  { id: 'ai_150', type: 'analysis', amount: 150, priceCents: 2500, price: 2500, priceId: 'price_1SuFTL36ErS8cGmLR8C0riJT', label: '150 AI Analyses' },
  { id: 'audio_60', type: 'audio', amount: 60, priceCents: 1000, price: 1000, priceId: 'price_1SuFTc36ErS8cGmLmTRZh88B', label: '60 Audio Minutes' },
  { id: 'audio_180', type: 'audio', amount: 180, priceCents: 2500, price: 2500, priceId: 'price_1SuFTs36ErS8cGmLOz3gmdjD', label: '180 Audio Minutes' },
];

export function getCreditPackById(id: string): CreditPack | undefined {
  return CREDIT_PACKS.find(p => p.id === id);
}

// ── Runtime Integrity Assertions ──────────────────────────────────────────────

/**
 * Validate that all paid plans with price IDs are registered in PRICE_TO_TIER.
 * Call this at app startup to catch config drift.
 */
export function validatePlansIntegrity(): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  for (const plan of PLANS) {
    if (plan.monthlyPriceId && !PRICE_TO_TIER[plan.monthlyPriceId]) {
      errors.push(`Plan "${plan.tier}" has monthlyPriceId "${plan.monthlyPriceId}" not in PRICE_TO_TIER`);
    }
    if (plan.annualPriceId && !PRICE_TO_TIER[plan.annualPriceId]) {
      errors.push(`Plan "${plan.tier}" has annualPriceId "${plan.annualPriceId}" not in PRICE_TO_TIER`);
    }
    // Ensure PRICE_TO_TIER entries point back to the correct tier
    if (plan.monthlyPriceId && PRICE_TO_TIER[plan.monthlyPriceId]?.tier !== plan.tier) {
      errors.push(`PRICE_TO_TIER entry for "${plan.monthlyPriceId}" maps to "${PRICE_TO_TIER[plan.monthlyPriceId]?.tier}" but plan says "${plan.tier}"`);
    }
    if (plan.annualPriceId && PRICE_TO_TIER[plan.annualPriceId]?.tier !== plan.tier) {
      errors.push(`PRICE_TO_TIER entry for "${plan.annualPriceId}" maps to "${PRICE_TO_TIER[plan.annualPriceId]?.tier}" but plan says "${plan.tier}"`);
    }
  }

  // Check for orphan entries in PRICE_TO_TIER (price IDs not referenced by any plan)
  const allPlanPriceIds = PLANS.flatMap(p => [p.monthlyPriceId, p.annualPriceId].filter(Boolean));
  for (const priceId of Object.keys(PRICE_TO_TIER)) {
    if (!allPlanPriceIds.includes(priceId)) {
      errors.push(`PRICE_TO_TIER contains orphan price ID "${priceId}" not referenced by any plan`);
    }
  }

  return { valid: errors.length === 0, errors };
}
